import React, { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import ActionMenu from './ActionMenu';
import { SkeletonTable } from './Skeletons';
import Pagination from './Table/Pagination';
import EmptyState from './EmptyState';
import { ArrowUp, ArrowDown, ArrowUpDown, Check, Minus } from 'lucide-react';
import { useViewport } from '../hooks/useViewport';

/**
 * Enterprise Data Table Component
 * Features:
 * - Clean, scannable layout with proper spacing
 * - Column sorting (click headers)
 * - Row selection with checkboxes
 * - Bulk actions toolbar
 * - Pagination
 * - Empty states with illustrations
 * - Mobile responsive
 *
 * @param {Array} columns - Column definitions: { key, label, width, render, align, sortable }
 * @param {Array} data - Array of row objects
 * @param {Array} actions - Row actions: { label, icon, onClick, variant }
 * @param {boolean} loading - Show loading state
 * @param {string} emptyMessage - Message when no data
 * @param {Function} onActionClick - Callback for action menu items
 * @param {Object} pagination - { currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }
 * @param {Object} sorting - { column, direction, onSort }
 * @param {Object} selection - { enabled, selectedIds, onSelectionChange, bulkActions, onBulkAction }
 * @param {Object} emptyState - { icon, title, description, action }
 * @param {string} mobileVariant - 'auto' (default — current hide-cols), 'card' (Phase 1.7a: card list on phone), or 'hide-cols'
 *   - In 'card' mode, columns may declare `primary: true` (card title),
 *     `secondary: true` (subtitle), `meta: true` (small chip).
 *     Unmarked columns are hidden on phone.
 */
export default function Table({
  columns = [],
  data = [],
  actions = [],
  loading = false,
  emptyMessage = 'No data available',
  onActionClick,
  // New enterprise features
  pagination = null,
  sorting = null,
  selection = null,
  emptyState = null,
  isRTL: _isRTLProp,
  rowKey = 'id', // Key to identify rows
  t: _tProp, // Translation function
  onRowClick = null, // Callback when a row is clicked
  actionColumnWidth = 56, // Width of the actions column in px
  mobileVariant = 'auto' // Phase 1.7a — 'card' | 'hide-cols' | 'auto'
}) {
  const { t: tI18n, i18n } = useTranslation();
  const t = _tProp || tI18n;
  const isRTL = _isRTLProp !== undefined ? _isRTLProp : (i18n.language === 'ar' || i18n.language === 'he');
  const { isMobile: isPhone } = useViewport();
  const renderAsCards = mobileVariant === 'card' && isPhone;

  // Local sorting state (if not controlled externally)
  const [localSortColumn, setLocalSortColumn] = useState(null);
  const [localSortDirection, setLocalSortDirection] = useState(null);

  // Selection state
  const selectedIds = selection?.selectedIds || new Set();
  const isSelectable = selection?.enabled;

  // Determine sort state
  const sortColumn = sorting?.column || localSortColumn;
  const sortDirection = sorting?.direction || localSortDirection;

  // Sort data locally if no external sorting handler
  const sortedData = useMemo(() => {
    if (!sortColumn || sorting?.onSort) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];

      if (aVal === bVal) return 0;
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      const comparison = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortDirection === 'desc' ? -comparison : comparison;
    });
  }, [data, sortColumn, sortDirection, sorting?.onSort]);

  // Handle column header click for sorting
  const handleSort = useCallback((column) => {
    if (!column.sortable) return;

    const newDirection =
      sortColumn === column.key
        ? sortDirection === 'asc'
          ? 'desc'
          : sortDirection === 'desc'
            ? null
            : 'asc'
        : 'asc';

    if (sorting?.onSort) {
      sorting.onSort(column.key, newDirection);
    } else {
      setLocalSortColumn(newDirection ? column.key : null);
      setLocalSortDirection(newDirection);
    }
  }, [sortColumn, sortDirection, sorting]);

  // Handle row selection
  const handleSelectRow = useCallback((rowId) => {
    if (!selection?.onSelectionChange) return;

    const newSelected = new Set(selectedIds);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    selection.onSelectionChange(newSelected);
  }, [selectedIds, selection]);

  // Handle select all
  const handleSelectAll = useCallback(() => {
    if (!selection?.onSelectionChange) return;

    if (selectedIds.size === sortedData.length) {
      selection.onSelectionChange(new Set());
    } else {
      const allIds = new Set(sortedData.map(row => row[rowKey]));
      selection.onSelectionChange(allIds);
    }
  }, [selectedIds, sortedData, selection, rowKey]);

  // Check if all rows are selected
  const isAllSelected = sortedData.length > 0 && selectedIds.size === sortedData.length;
  const isSomeSelected = selectedIds.size > 0 && selectedIds.size < sortedData.length;

  // Loading state
  if (loading) {
    return (
      <div style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: 'var(--border-radius-md)',
        border: '1px solid var(--color-border)',
        overflow: 'hidden'
      }}>
        <SkeletonTable
          rows={5}
          columns={columns.length}
          hasActions={actions && actions.length > 0}
        />
      </div>
    );
  }

  // Empty state
  if (!data || data.length === 0) {
    if (emptyState) {
      return (
        <div style={{
          backgroundColor: 'var(--color-surface)',
          borderRadius: 'var(--border-radius-md)',
          border: '1px solid var(--color-border)',
          overflow: 'hidden'
        }}>
          <EmptyState {...emptyState} />
        </div>
      );
    }

    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 'var(--spacing-8)',
        color: 'var(--color-text-tertiary)',
        textAlign: 'center'
      }}>
        {emptyMessage}
      </div>
    );
  }

  // Calculate column widths.
  //
  // Column `width` values are authored as percentages ("25%", "20%"…) that are
  // meant to sum to ~100% of the row. But the selection checkbox (48px) and the
  // actions column sit OUTSIDE that budget, so the grid always came out ~104px
  // wider than its container: every table rendered a permanent horizontal
  // scrollbar and, in RTL, clipped the "פעולות" actions header down to one letter.
  //
  // Translating the percentages into `fr` makes them shares of whatever space
  // is left after the fixed columns, preserving their relative proportions
  // exactly. minmax(0, …) is what stops a long cell value from forcing its
  // track wider than that share — grid tracks default to `min-width: auto`,
  // which is the other half of why these tables overflowed.
  const toTrack = (w) => {
    const raw = String(w == null ? 'auto' : w).trim();
    const pct = /^([\d.]+)%$/.exec(raw);
    if (pct) return `minmax(0, ${parseFloat(pct[1]) / 10}fr)`;
    if (raw === 'auto') return 'minmax(0, auto)';
    return raw;
  };
  const columnWidths = columns.map(col => toTrack(col.width));
  const hasActions = actions && actions.length > 0;

  // ===========================================================
  // Phase 1.7a — Mobile card variant render branch
  // Triggered by `mobileVariant="card"` on phones. Columns annotate
  // their role: primary (title), secondary (subtitle), meta (chip).
  // ===========================================================
  if (renderAsCards) {
    const primaryCols = columns.filter(c => c.primary);
    const secondaryCols = columns.filter(c => c.secondary);
    const metaCols = columns.filter(c => c.meta);
    const renderVal = (col, row) => (col.render ? col.render(row) : row[col.key] ?? '—');

    return (
      <div style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
        {/* Bulk Actions Toolbar — phone */}
        {isSelectable && selectedIds.size > 0 && selection?.bulkActions && (
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.625rem 0.75rem',
            backgroundColor: 'var(--primary-dark)',
            color: 'var(--color-on-primary, #fff)',
            borderRadius: 'var(--radius-md)',
            marginBottom: 'var(--space-3)',
            boxShadow: 'var(--shadow-md)',
            flexWrap: 'wrap',
          }}>
            <span style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>
              {selectedIds.size} {t('common.selected')}
            </span>
            <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
              {selection.bulkActions.map((action) => (
                <button
                  key={action.key}
                  onClick={() => selection.onBulkAction?.(action.key, selectedIds)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: action.variant === 'danger' ? 'rgba(239,68,68,0.25)' : 'rgba(255,255,255,0.18)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '0.5rem',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 600,
                    cursor: 'pointer',
                    minHeight: 44,
                  }}
                >
                  {action.icon && <action.icon size={16} />}
                  {action.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => selection.onSelectionChange?.(new Set())}
              style={{
                marginInlineStart: 'auto',
                padding: '0.5rem 0.625rem',
                backgroundColor: 'transparent',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.4)',
                borderRadius: '0.5rem',
                fontSize: 'var(--font-size-xs)',
                cursor: 'pointer',
                minHeight: 36,
              }}
              aria-label={t('common.clearSelection')}
            >
              ✕
            </button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
          {sortedData.map((row, rowIdx) => {
            const rowId = row[rowKey];
            const isSelected = selectedIds.has(rowId);
            const onCardClick = (e) => {
              // Don't trigger row click if user tapped the action menu or checkbox
              if (e.target.closest('[data-table-card-action]')) return;
              if (e.target.closest('[data-table-card-check]')) return;
              if (isSelectable && selectedIds.size > 0) {
                handleSelectRow(rowId);
                return;
              }
              if (onRowClick) onRowClick(row);
            };
            return (
              <article
                key={rowId || rowIdx}
                onClick={onCardClick}
                style={{
                  background: isSelected ? 'var(--primary-50)' : 'var(--surface-raised)',
                  border: `1px solid ${isSelected ? 'var(--primary-dark)' : 'var(--border-light)'}`,
                  borderRadius: 'var(--radius-lg)',
                  padding: 'var(--space-3) var(--space-4)',
                  display: 'grid',
                  gridTemplateColumns: isSelectable
                    ? '24px 1fr auto'
                    : '1fr auto',
                  gap: 'var(--space-3)',
                  alignItems: 'center',
                  boxShadow: 'var(--shadow-xs)',
                  cursor: onRowClick || isSelectable ? 'pointer' : 'default',
                  transition: 'transform var(--motion-quick), border-color var(--motion-quick), box-shadow var(--motion-quick)',
                }}
              >
                {isSelectable && (
                  <div
                    data-table-card-check
                    className={`enterprise-checkbox ${isSelected ? 'checked' : ''}`}
                    onClick={(e) => { e.stopPropagation(); handleSelectRow(rowId); }}
                  >
                    {isSelected && <Check size={12} color="var(--color-on-primary, #fff)" strokeWidth={3} />}
                  </div>
                )}
                <div style={{ minWidth: 0 }}>
                  {primaryCols.map((col) => (
                    <div
                      key={col.key}
                      style={{
                        fontWeight: 600,
                        fontSize: 'var(--font-size-base)',
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        lineHeight: 1.3,
                      }}
                    >
                      {renderVal(col, row)}
                    </div>
                  ))}
                  {secondaryCols.length > 0 && (
                    <div style={{
                      marginTop: 2,
                      display: 'flex',
                      gap: 'var(--space-2)',
                      flexWrap: 'wrap',
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--text-secondary)',
                    }}>
                      {secondaryCols.map((col) => (
                        <span key={col.key}>{renderVal(col, row)}</span>
                      ))}
                    </div>
                  )}
                  {metaCols.length > 0 && (
                    <div style={{
                      marginTop: 6,
                      display: 'flex',
                      gap: 'var(--space-2)',
                      flexWrap: 'wrap',
                    }}>
                      {metaCols.map((col) => (
                        <span
                          key={col.key}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            padding: '2px 8px',
                            background: 'var(--neutral-100)',
                            color: 'var(--text-secondary)',
                            borderRadius: 'var(--radius-full)',
                            fontSize: 'var(--font-size-xs)',
                            fontWeight: 500,
                          }}
                        >
                          {renderVal(col, row)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                {hasActions && (
                  <div data-table-card-action onClick={(e) => e.stopPropagation()}>
                    <ActionMenu
                      actions={actions
                        .filter(action => !(typeof action.hidden === 'function' ? action.hidden(row) : action.hidden))
                        .map(action => ({
                          ...action,
                          onClick: () => {
                            if (action.onClick) action.onClick(row);
                            if (onActionClick) onActionClick(action.key, row);
                          }
                        }))}
                    />
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {pagination && (
          <div style={{ marginTop: 'var(--space-3)' }}>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={pagination.pageSize}
              onPageChange={pagination.onPageChange}
              onPageSizeChange={pagination.onPageSizeChange}
            />
          </div>
        )}
      </div>
    );
  }
  // ===========================================================

  // Build grid template with selection and actions columns
  let gridTemplate = '';
  if (isSelectable) gridTemplate += '48px ';
  gridTemplate += columnWidths.join(' ');
  if (hasActions) gridTemplate += ` ${actionColumnWidth}px`;

  return (
    <>
      <style>{`
        .enterprise-table-header {
          display: grid;
          min-width: 100%;
          position: sticky;
          top: 0;
          z-index: 10;
        }
        .enterprise-table-body {
          display: grid;
          min-width: 100%;
        }
        .enterprise-table-row {
          ${onRowClick ? 'cursor: pointer;' : ''}
        }
        .enterprise-table-row:hover .enterprise-table-cell {
          background-color: var(--color-surface-alt) !important;
        }
        .sortable-header {
          cursor: pointer;
          user-select: none;
        }
        .sortable-header:hover {
          background-color: var(--color-border);
        }
        .checkbox-cell {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .enterprise-checkbox {
          width: 18px;
          height: 18px;
          border: 2px solid var(--color-border);
          border-radius: 4px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.15s ease;
          background-color: var(--color-surface);
        }
        .enterprise-checkbox:hover {
          border-color: var(--color-primary);
        }
        .enterprise-checkbox.checked {
          background-color: var(--color-primary);
          border-color: var(--color-primary);
        }
        .enterprise-checkbox.indeterminate {
          background-color: var(--color-primary);
          border-color: var(--color-primary);
        }
        @media (max-width: 768px) {
          .enterprise-table-header {
            grid-template-columns: 1fr 60px !important;
          }
          .enterprise-table-body > div {
            grid-template-columns: 1fr 60px !important;
          }
          .enterprise-table-cell:not(:first-child):not(:last-child) {
            display: none !important;
          }
          .checkbox-cell {
            display: none !important;
          }
          .enterprise-table-header .checkbox-cell {
            display: none !important;
          }
        }
      `}</style>

      {/* Bulk Actions Toolbar */}
      {isSelectable && selectedIds.size > 0 && selection?.bulkActions && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '1rem',
          padding: '0.75rem 1rem',
          backgroundColor: 'var(--color-primary)',
          color: 'var(--color-on-primary, #fff)',
          borderRadius: 'var(--radius-md) var(--radius-md) 0 0',
          marginBottom: '-1px',
          direction: isRTL ? 'rtl' : 'ltr'
        }}>
          <span style={{ fontWeight: '500' }}>
            {selectedIds.size} {t('common.selected')}
          </span>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {selection.bulkActions.map((action) => (
              <button
                key={action.key}
                onClick={() => selection.onBulkAction?.(action.key, selectedIds)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.375rem 0.75rem',
                  backgroundColor: action.variant === 'danger' ? 'rgba(239,68,68,0.2)' : 'rgba(255,255,255,0.15)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  fontSize: '0.85rem',
                  cursor: 'pointer'
                }}
              >
                {action.icon && <action.icon size={14} />}
                {action.label}
              </button>
            ))}
          </div>
          <button
            onClick={() => selection.onSelectionChange?.(new Set())}
            style={{
              marginInlineStart: 'auto',
              padding: '0.375rem 0.75rem',
              backgroundColor: 'transparent',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '0.375rem',
              fontSize: '0.85rem',
              cursor: 'pointer'
            }}
          >
            {t('common.clearSelection')}
          </button>
        </div>
      )}

      <div style={{
        backgroundColor: 'var(--color-surface)',
        borderRadius: selectedIds.size > 0 && selection?.bulkActions ? '0 0 var(--radius-md) var(--radius-md)' : 'var(--radius-md)',
        boxShadow: 'var(--shadow-base)',
        border: '1px solid var(--color-border)',
        position: 'relative',
        overflow: 'hidden',
        direction: isRTL ? 'rtl' : 'ltr'
      }}>
        {/* Table wrapper */}
        <div style={{ width: '100%', overflowX: 'auto' }}>
          {/* Table Header */}
          <div className="enterprise-table-header" style={{
            display: 'grid',
            gridTemplateColumns: gridTemplate,
            gap: 0,
            backgroundColor: 'var(--color-surface-alt)',
            borderBottom: '1px solid var(--color-border)',
            minWidth: '100%'
          }}>
            {/* Select All Checkbox */}
            {isSelectable && (
              <div className="checkbox-cell" style={{ padding: 'var(--spacing-4)' }}>
                <div
                  className={`enterprise-checkbox ${isAllSelected ? 'checked' : isSomeSelected ? 'indeterminate' : ''}`}
                  onClick={handleSelectAll}
                >
                  {isAllSelected && <Check size={12} color="var(--color-on-primary, #fff)" strokeWidth={3} />}
                  {isSomeSelected && <Minus size={12} color="var(--color-on-primary, #fff)" strokeWidth={3} />}
                </div>
              </div>
            )}

            {/* Column Headers */}
            {columns.map((col) => (
              <div
                key={col.key}
                className={col.sortable ? 'sortable-header' : ''}
                onClick={() => col.sortable && handleSort(col)}
                style={{
                  padding: 'var(--spacing-4)',
                  fontWeight: 'var(--font-weight-semibold)',
                  color: 'var(--color-text-tertiary)',
                  fontSize: 'var(--font-size-xs)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  textAlign: col.align || (isRTL ? 'right' : 'left'),
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  transition: 'background-color 0.15s ease'
                }}
              >
                {col.label}
                {col.sortable && (
                  <span style={{ color: sortColumn === col.key ? 'var(--color-primary)' : 'var(--color-text-tertiary)' }}>
                    {sortColumn === col.key ? (
                      sortDirection === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />
                    ) : (
                      <ArrowUpDown size={14} style={{ opacity: 0.5 }} />
                    )}
                  </span>
                )}
              </div>
            ))}

            {/* Actions Header */}
            {hasActions && (
              <div style={{
                padding: 'var(--spacing-4)',
                fontWeight: 'var(--font-weight-semibold)',
                color: 'var(--color-text-tertiary)',
                fontSize: 'var(--font-size-xs)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                textAlign: 'center'
              }}>
                {t('common.actions')}
              </div>
            )}
          </div>

          {/* Table Body */}
          <div className="enterprise-table-body">
            {sortedData.map((row, rowIdx) => {
              const rowId = row[rowKey];
              const isSelected = selectedIds.has(rowId);

              return (
                <div
                  key={rowId || rowIdx}
                  className="enterprise-table-row"
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: gridTemplate,
                    backgroundColor: isSelected ? 'var(--color-primary-light, rgba(37, 99, 235, 0.05))' : 'var(--color-surface)',
                    transition: 'background-color 0.15s ease'
                  }}
                >
                  {/* Selection Checkbox */}
                  {isSelectable && (
                    <div className="enterprise-table-cell checkbox-cell"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: 'var(--spacing-4)',
                        borderBottom: '1px solid var(--color-border)'
                      }}>
                      <div
                        className={`enterprise-checkbox ${isSelected ? 'checked' : ''}`}
                        onClick={(e) => { e.stopPropagation(); handleSelectRow(rowId); }}
                      >
                        {isSelected && <Check size={12} color="var(--color-on-primary, #fff)" strokeWidth={3} />}
                      </div>
                    </div>
                  )}

                  {/* Data Cells */}
                  {columns.map((col) => (
                    <div
                      key={`${rowIdx}-${col.key}`}
                      className="enterprise-table-cell"
                      style={{
                        padding: 'var(--spacing-4)',
                        color: 'var(--color-text-primary)',
                        fontSize: 'var(--font-size-sm)',
                        display: 'flex',
                        alignItems: 'center',
                        borderBottom: '1px solid var(--color-border)',
                        textAlign: col.align || (isRTL ? 'right' : 'left'),
                        wordBreak: 'break-word',
                        backgroundColor: 'inherit',
                        transition: 'background-color 0.15s ease'
                      }}
                    >
                      {col.render ? col.render(row) : row[col.key] || '—'}
                    </div>
                  ))}

                  {/* Actions Cell */}
                  {hasActions && (
                    <div
                      className="enterprise-table-cell"
                      onClick={(e) => e.stopPropagation()}
                      style={{
                        padding: 'var(--spacing-4)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderBottom: '1px solid var(--color-border)',
                        backgroundColor: 'inherit'
                      }}
                    >
                      <ActionMenu
                        actions={actions
                          .filter(action => !(typeof action.hidden === 'function' ? action.hidden(row) : action.hidden))
                          .map(action => ({
                            ...action,
                            onClick: () => {
                              if (action.onClick) action.onClick(row);
                              if (onActionClick) onActionClick(action.key, row);
                            }
                          }))}
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Pagination */}
        {pagination && (
          <div style={{
            borderTop: '1px solid var(--color-border)',
            padding: '0 1rem'
          }}>
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={pagination.pageSize}
              onPageChange={pagination.onPageChange}
              onPageSizeChange={pagination.onPageSizeChange}
              isRTL={isRTL}
              t={t}
            />
          </div>
        )}
      </div>
    </>
  );
}
